import { graphql } from "react-apollo";
import makePromiseQueue, {
  abortErrorMessage,
} from "web/core/utils/makePromiseQueue";

const queue = makePromiseQueue();

/**
 * @callback GraphqlWithPreloadHOC
 * @param {import('react').ComponentType} WrappedComponent
 * @returns {import('react').ComponentClass}
 */

/**
 * @typedef {Object} PreloadOperationOptions
 * @property {Function | Object} [preloadOptions]
 */

/**
 * @callback GraphqlWithPreload
 * @param {import('graphql').DocumentNode} query
 * @param {import('react-apollo').OperationOption & PreloadOperationOptions} options
 * @returns {GraphqlWithPreloadHOC}
 */

/** @type {GraphqlWithPreload} */
const graphqlWithPreload =
  (
    query,
    { options, preloadOptions, ...rest } = {
      options: null,
      preloadOptions: null,
    }
  ) =>
  (BaseComponent) => {
    const Component = graphql(query, { options, ...rest })(BaseComponent);

    /** @type {import('apollo-client').ApolloClient | null} */
    // @ts-ignore
    const apolloClient = process.env.SERVER ? null : window.apolloClient;

    if (!process.env.SERVER && !apolloClient) {
      console.warn(
        "No window.apolloClient defined. It should be defined in the `web/makeApp.js`."
      );
      return Component;
    }

    // Becareful here, the preload does not necessarily return apollo's result.
    // In the promise queue, some old elements in the queue can be aborted with an error.
    // We will catch the error in this case because this is how it is intended to work.
    // Other errors like network issues will still fail.
    // @ts-ignore
    Component.preload = (props) => {
      return queue
        .add(() => {
          return apolloClient.query({
            query: query,
            ...(typeof preloadOptions === "function"
              ? preloadOptions(props)
              : {}),
          });
        })
        .catch((e) => {
          if (!e || e.message !== abortErrorMessage) {
            throw e;
          }
        });
    };

    return Component;
  };

export default graphqlWithPreload;
